import React from 'react';

import { WebsocketMessage } from '../../../../../../typings/websocket-messages';
import { assertNever } from '../../../../utils/variables';
import { AssetManager } from '../../../../viewer/asset-manager';
import { Design } from '../../../../viewer/elements/design';
import { Dxf } from '../../../../viewer/elements/dxf';
import { AssetType, ElementObj } from '../../../../viewer/elements/element-obj';
import { Geometry } from '../../../../viewer/elements/geometry';
import { Glb } from '../../../../viewer/elements/glb';
import { Model } from '../../../../viewer/elements/model';
import { Poi } from '../../../../viewer/elements/poi/poi';
import { TerrainModel } from '../../../../viewer/elements/terrain-model';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { ViewSettings } from '../../../../viewer/view-settings';
import { useSubscribe } from '../../../hooks/use-subscribe';
import { projectStateAdapter } from '../../../state/project-state/project-state-adapter';
import { ThemeManager } from '../../../themes/theme-manager';

type AssetTypeToModelMap = {
  area: Geometry[];
  line: Geometry[];
  design: Design[];
  poi: Poi[];
  dxf: Dxf[];
  glb: Glb[];
  model: Model[];
  'terrain-model': TerrainModel[];
};

type AssetsStore = {
  assets: ElementObj[];
  isLoading: boolean;
  isLoaded: boolean;
  errors: { message: string }[];
  clearErrors: () => void;
  filterAssetsByType: <T extends AssetType>(
    assetType: T,
    source?: ElementObj[],
  ) => AssetTypeToModelMap[T];
  findByAssetId: (assetId: string) => ElementObj | undefined;
  findByUuid: (uuid: string) => ElementObj | undefined;
};

const AssetsContext = React.createContext<AssetsStore | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

/**
 * Read-only AssetsProvider that mirrors assets from AssetManager.
 * This is an initial step towards moving asset state into React.
 */
export const AssetsProvider = (props: Props) => {
  const [assets, setAssets] = React.useState<ElementObj[]>(AssetManager.instance.getAssets());
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isLoaded, setIsLoaded] = React.useState<boolean>(AssetManager.instance.isLoaded());
  const [errors, setErrors] = React.useState<{ message: string }[]>([]);

  const refresh = React.useCallback(() => {
    setAssets(AssetManager.instance.getAssets());
  }, []);

  useSubscribe(SubscriptionTopic.AssetListChanged, () => {
    setIsLoaded(true);
    refresh();
  });

  // Initial load on mount based on current project id.
  React.useEffect(() => {
    const load = async () => {
      const projectId = projectStateAdapter.getSnapshot().projectId;
      if (!projectId) {
        setAssets([]);
        setIsLoaded(false);
        return;
      }

      if (!AssetManager.instance.isLoaded()) {
        setIsLoading(true);
        try {
          await AssetManager.instance.loadProjectAssets(
            projectId,
            ViewSettings.instance.isCadViewer ? ['Design', 'Dxf', 'Glb', 'Poi'] : undefined,
          );

          // In shared view we want line and areas to be displayed automatically.
          if (ThemeManager.instance.isSharedView) {
            const lineAndAreas = [
              ...AssetManager.instance.getAssetsByType('line'),
              ...AssetManager.instance.getAssetsByType('area'),
            ];
            for (const asset of lineAndAreas) {
              if (asset.getStateVisibility()) {
                asset.loadGeometry();
              }
            }
          }

          AssetManager.instance.setVisible(true);
          setIsLoaded(true);
          refresh();
        } catch (err) {
          setErrors((prev) => [...prev, { message: String(err) }]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoaded(true);
        refresh();
      }
    };

    void load();
  }, [refresh]);

  useSubscribe(SubscriptionTopic.WebsocketMessageRecieved, (data: WebsocketMessage) => {
    const currentProjectId = projectStateAdapter.getSnapshot().projectId;
    if (!currentProjectId) {
      return;
    }

    if ((data as { projectId?: string }).projectId !== currentProjectId) {
      return;
    }

    switch (data.message) {
      case 'DynamicAssetUpdated': {
        const id = data.dynamicAssetId;
        setIsLoading(true);

        // For now, let asset manager handle the update as before. Keep the change in this file minimal for now. This will change in the future.
        void AssetManager.instance
          .updateAsset(id, 'DynamicAsset')
          .catch((err) => setErrors((prev) => [...prev, { message: String(err) }]))
          .finally(() => setIsLoading(false));
        break;
      }
      case 'PoiUpdated': {
        const id = data.poiId;
        setIsLoading(true);
        void (async () => {
          try {
            // For now, let asset manager handle the update as before. Keep the change in this file minimal for now. This will change in the future.
            await AssetManager.instance.updateAsset(id, 'Poi');
            AssetManager.instance.publishAssetListChanged();
          } catch (err) {
            setErrors((prev) => [...prev, { message: String(err) }]);
          } finally {
            setIsLoading(false);
          }
        })();
        break;
      }
      default:
        break;
    }
  });

  const filterAssetsByType = React.useCallback(
    <T extends AssetType>(assetType: T, source?: ElementObj[]) => {
      const list = source ?? assets;
      const filtered = list.filter((asset) => {
        switch (assetType) {
          case 'area':
            return asset instanceof Geometry && asset.isArea;
          case 'line':
            return asset instanceof Geometry && asset.isArea === false;
          case 'design':
            return asset instanceof Design;
          case 'poi':
            return asset instanceof Poi;
          case 'dxf':
            return asset instanceof Dxf;
          case 'glb':
            return asset instanceof Glb;
          case 'model':
            return asset instanceof Model;
          case 'terrain-model':
            return asset instanceof TerrainModel;
          default:
            assertNever(assetType);
        }
      });
      return filtered as AssetTypeToModelMap[T];
    },
    [assets],
  );

  const findByAssetId = React.useCallback(
    (assetId: string) => assets.find((a) => a.assetId === assetId),
    [assets],
  );

  const findByUuid = React.useCallback((id: string) => assets.find((a) => a.uuid === id), [assets]);

  const value = React.useMemo<AssetsStore>(
    () => ({
      assets,
      isLoading,
      isLoaded,
      errors,
      clearErrors: () => setErrors([]),
      filterAssetsByType,
      findByAssetId,
      findByUuid,
    }),
    [assets, isLoading, isLoaded, errors, filterAssetsByType, findByAssetId, findByUuid],
  );

  return <AssetsContext.Provider value={value}>{props.children}</AssetsContext.Provider>;
};

export function useAssetsStore() {
  const ctx = React.useContext(AssetsContext);
  if (!ctx) {
    throw new Error('useAssetsStore must be used within an AssetsProvider');
  }
  return ctx;
}
